/**
 * Canonical paid-status → {label, color} mapping for any UI that renders a
 * transaction's payment status badge (Sales tab list, detail header, etc.).
 *
 * Mirrors the shape of `clientFormStatusTag` (utils/clientFormStatus.ts): a
 * `{label, color}` switch where `color` is a Mantine palette key so callers can
 * pass it straight to `<Badge color={...} />`.
 *
 * Combines the two independent transaction axes:
 *   - `transaction_status` (money-in): `unpaid | partially_paid | paid | void`
 *   - `refund_status`      (money-out): `none | partial | full`
 *
 * A FULL refund overrides the paid badge to "Refunded" (money returned). A
 * PARTIAL refund leaves the paid badge intact — pair this util with the
 * existing `RefundStatusBadge` (components/common/RefundStatusBadge) which
 * renders the "Partially refunded" overlay from the same `refund_status` axis.
 * Do NOT fork clientFormStatus; this follows its pattern for the payment axis.
 */
export type TransactionPaidStatus =
  | "unpaid"
  | "partially_paid"
  | "paid"
  | "void"
  | string;

export type TransactionRefundStatus =
  | "none"
  | "partial"
  | "full"
  | null
  | undefined;

export function paymentStatusTag(
  transactionStatus: TransactionPaidStatus,
  refundStatus: TransactionRefundStatus = "none",
): { label: string; color: string } {
  // Void (Sales tab) takes the transaction out of every report; it wins over
  // any refund state.
  if (transactionStatus === "void") {
    return { label: "Voided", color: "gray" };
  }

  // Full refund is terminal for money-in — surface it as the paid badge so the
  // list reads correctly at a glance; the RefundStatusBadge overlay is then
  // redundant for the full case and callers may omit it.
  if (refundStatus === "full") {
    return { label: "Refunded", color: "gray" };
  }

  switch (transactionStatus) {
    case "paid":
      return { label: "Paid", color: "green" };
    case "partially_paid":
      return { label: "Partially paid", color: "yellow" };
    case "unpaid":
      return { label: "Unpaid", color: "red" };
    default:
      return {
        label:
          typeof transactionStatus === "string" && transactionStatus.length > 0
            ? transactionStatus.replace(/_/g, " ")
            : "Unknown",
        color: "gray",
      };
  }
}
